import { useNavigate } from "react-router";
import { checkForAndReplaceNullEmptyWhitespace } from "utils/string";
import { ETAText } from "constants/text";
import { brandCheck, getOrderTypeDisplayLabel, isMerchOrCampaignOrder } from "utils/order";
import StyledSummaryCardLogo from "styles/StyledComponents/StyledSummaryCardLogo";
import {
	OrderSummaryCardChipList,
	OrderSummaryCardContent,
	OrderSummaryCardDetails,
	OrderSummaryCardHeader,
	OrderSummaryCardHeaderContents,
	OrderSummaryCardHeaderShippingDetails,
	OrderSummaryCardJobName,
	OrderSummaryCardShippingDetailsContainer,
	OrderSummaryCardWrapper
} from "components/OrderSummaryCard/OrderSummaryCard.styles.ts";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { OrderStatus, OrderType } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";
import { getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { OrderSummaryChip } from "components/OrderSummaryCard/components/OrderSummaryShip/OrderSummaryChip.tsx";
import { OrderSummaryCardShippingDetails } from "components/OrderSummaryCard/components/OrderSummaryCardShippingDetails/OrderSummaryCardShippingDetails.tsx";
import { buildOrderChips, buildOrderDetails } from "components/OrderSummaryCard/utils/OrderSummaryCard.utils.ts";

interface OrderSummaryCardProps {
	order: models["OrderViewModel"] | null | undefined;
}

const OrderSummaryCard = ({ order }: OrderSummaryCardProps) => {
	const navigateToDetails = useNavigate();

	if (!order) return null;

	const orderGlobals = getSingleOrderGlobal(order);
	const orderDetails = buildOrderDetails(order);
	const orderChips = buildOrderChips(order);
	const isMerchOrCampaignOrderType = isMerchOrCampaignOrder(order.orderType);
	const showETA = order.orderType !== OrderType.BILLING_ONLY && order.status !== OrderStatus.CANCELLED;

	return (
		<OrderSummaryCardWrapper
			key={order.orderId}
			data-testid="single-order-card"
			data-id="single-order-card"
			data-accountnumber={order.accountNumber}
			data-designer={order.designer?.designerNumber}
			data-brand={orderGlobals?.brand?.description?.replaceAll("_", " ")?.toLowerCase()}
			data-status={order.status?.replaceAll("_", " ")?.toLowerCase()}
			data-ordertype={getOrderTypeDisplayLabel(order.orderType)?.toLowerCase()}
			role="button"
			onClick={() => navigateToDetails(`/details/${order.orderId}`)}
		>
			<OrderSummaryCardContent>
				<OrderSummaryCardHeader>
					<OrderSummaryCardJobName data-testid="single-card-job-name-order-number">
						{order.jobName ?? order.poNumber}
					</OrderSummaryCardJobName>
					<OrderSummaryCardHeaderContents>
						{!isMerchOrCampaignOrderType && (
							<StyledSummaryCardLogo
								data-testid="single-card-brand-logo"
								parentBrand={orderGlobals?.brand?.code}
								src={
									brandCheck(orderGlobals?.brand?.code)
										? `/assets/manufacture_logos/${orderGlobals?.brand?.code}.png`
										: "/assets/tandem_logos/cwg_logo.png"
								}
								alt={
									brandCheck(orderGlobals?.brand?.code)
										? (orderGlobals?.brand?.code ?? "")
										: "CabinetworksGroup Logo"
								}
							/>
						)}
						<OrderSummaryCardChipList>
							{orderChips.map((chip) => (
								<OrderSummaryChip
									key={chip.label}
									chip={chip}
								/>
							))}
						</OrderSummaryCardChipList>
						<OrderSummaryCardHeaderShippingDetails>
							{showETA && (
								<OrderSummaryCardShippingDetailsContainer style={{ minWidth: "140px" }}>
									<span className="subtitle1">{ETAText} &nbsp;</span>
									<span data-testid="single-card-eta">{order.eta}</span>
								</OrderSummaryCardShippingDetailsContainer>
							)}
							<OrderSummaryCardShippingDetails shippingDetails={order.shippingDetails} />
						</OrderSummaryCardHeaderShippingDetails>
					</OrderSummaryCardHeaderContents>
				</OrderSummaryCardHeader>
				<OrderSummaryCardDetails isMerchOrder={isMerchOrCampaignOrderType}>
					{orderDetails.map((detail) => (
						<div key={detail.label}>
							<span>{detail.label}</span>
							<span data-testid={detail.testId}>
								{checkForAndReplaceNullEmptyWhitespace(detail.value)}
							</span>
						</div>
					))}
				</OrderSummaryCardDetails>
			</OrderSummaryCardContent>
			<ChevronRight data-testid="order-summary-detail-arrow-link" />
		</OrderSummaryCardWrapper>
	);
};

export default OrderSummaryCard;
